/**
 * Wire DTO → domain conversion for Reward Center V2.
 *
 * Rules that hold everywhere in this file:
 *   • Decimals stay strings. Only counters the contract types as `number`
 *     (`percent`, `participant_count`, pagination) are numbers.
 *   • `int64`-as-string is converted only after a safe-range check, and only
 *     for values we genuinely need as numbers (countdown seconds).
 *   • Everything optional gets a fallback, because campaign/task metadata is
 *     not validated by protobuf.
 */
import { formatRewardLabel } from './labels';
import {
  ActivityStatusValues,
  Campaign,
  CampaignHistoryEntry,
  CampaignHistoryEntryDto,
  CampaignMeta,
  CampaignStatusValues,
  CampaignViewDto,
  ClaimModeValues,
  ClaimStatusValues,
  DecimalString,
  HistoryItem,
  HistoryItemDto,
  Int64String,
  ListBodyDto,
  ListEnvelopeDto,
  Lottery,
  LotteryListItemDto,
  LotteryPrize,
  LotteryPrizeDto,
  LotterySummary,
  LotterySummaryDto,
  LotteryWinner,
  LotteryWinnerDto,
  LOTTERY_TICKET_ASSET,
  Paged,
  RecurrenceValues,
  Reward,
  RewardDto,
  RewardHistoryEntry,
  RewardHistoryEntryDto,
  TaskHistoryEntry,
  TaskHistoryEntryDto,
  SpecialCampaignEnvelopeDto,
  Task,
  TaskGoal,
  TaskMutationResult,
  TaskMutationResultDto,
  TaskProgress,
  TaskProgressDto,
  TaskViewDto,
  toTaskDestination,
  UserTaskStatusValues
} from './types';

/** `{ data: T }` is the only successful business envelope in V2. */
export const unwrap = <T>(response: { data: T }): T => response?.data;

const DEFAULT_PAGE_LIMIT = 20;

/** Decimal fields must never become numbers — just guarantee a string. */
const decimal = (value?: DecimalString | null): DecimalString =>
  typeof value === 'string' && value.length > 0 ? value : '0';

/**
 * `int64` arrives as a string. Convert only inside the safe-integer range;
 * anything larger is a countdown longer than any campaign and clamps to 0.
 */
const int64 = (value?: Int64String | null): number => {
  if (typeof value !== 'string' || value.length === 0) return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || !Number.isSafeInteger(parsed)) return 0;
  return parsed > 0 ? parsed : 0;
};

const count = (value?: number | null): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const text = (value?: string | null): string =>
  typeof value === 'string' ? value : '';

const list = <T>(value?: T[] | null): T[] =>
  Array.isArray(value) ? value : [];

/**
 * Splits a free-text rules blob into steps. The recommended metadata shape is
 * a `rules` array, but the admin panel has also written a single `rules_text`
 * string, and V1 content used `&&` as the separator — accept all three.
 */
const splitRules = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((rule): rule is string => typeof rule === 'string');
  }
  if (typeof value !== 'string' || value.trim().length === 0) return [];
  return value
    .split(/&&|\r?\n/)
    .map((rule) => rule.trim())
    .filter((rule) => rule.length > 0);
};

const readString = (
  source: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = source?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const IMAGE_CDN_HOST = (() => {
  try {
    return new URL(import.meta.env.VITE_IMAGE_BASE_URL || '').host;
  } catch {
    return '';
  }
})();

/**
 * Admin-entered image URLs go straight into <img src>, so only https images
 * from our own hosts are kept: no http mixed content and no third-party
 * tracking pixels. Anything else is dropped and the bundled image shows.
 */
export const trustedImageUrl = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    const isOwnHost =
      url.host === window.location.host ||
      (!!IMAGE_CDN_HOST && url.host === IMAGE_CDN_HOST) ||
      /(^|\.)rabex\.(ir|co)$/.test(url.hostname);
    const isSecure =
      url.protocol === 'https:' || url.origin === window.location.origin;
    return isOwnHost && isSecure ? url.href : undefined;
  } catch {
    return undefined;
  }
};

export const normalizeReward = (dto: RewardDto): Reward => {
  const asset = text(dto?.asset);
  const amount = decimal(dto?.amount);
  return {
    key: text(dto?.key),
    asset,
    amount,
    unitWeight: decimal(dto?.unit_weight ?? '1'),
    // `display_name` is the only localized label the contract carries; without
    // it we compose one, so a raw code like "3 LOTTERY_TICKET" never reaches
    // the UI.
    displayName: text(dto?.display_name) || formatRewardLabel(amount, asset),
    isLotteryTicket: asset === LOTTERY_TICKET_ASSET
  };
};

const normalizeCampaignMeta = (
  metadata: Record<string, unknown> | undefined
): CampaignMeta => {
  const presentation = (metadata?.presentation ?? {}) as Record<
    string,
    unknown
  >;
  return {
    imageUrl: trustedImageUrl(readString(metadata, 'image_url')),
    description: readString(metadata, 'description'),
    rulesTitle: readString(metadata, 'rules_title'),
    rules: splitRules(metadata?.rules ?? metadata?.rules_text),
    rulesUrl: readString(metadata, 'rules_url'),
    heroTitle: readString(presentation, 'hero_title'),
    heroSubtitle: readString(presentation, 'hero_subtitle'),
    // Display-only announcement of the intended prize; see CampaignMeta.
    prizePreview: readString(presentation, 'prize_preview'),
    prizeIcon: trustedImageUrl(readString(presentation, 'prize_icon')),
    prizeAsset: readString(presentation, 'prize_asset')?.toUpperCase()
  };
};

export const normalizeCampaign = (dto: CampaignViewDto): Campaign => ({
  id: text(dto?.id),
  slug: text(dto?.slug),
  title: text(dto?.title),
  isSpecial: dto?.is_special === true,
  tags: list(dto?.tags),
  activityStatus: dto?.activity_status ?? ActivityStatusValues.INACTIVE,
  status: dto?.status ?? CampaignStatusValues.SCHEDULED,
  startsAt: dto?.starts_at ?? null,
  endsAt: dto?.ends_at ?? null,
  remainingSeconds: int64(dto?.remaining_seconds),
  progress: {
    requiredCompleted: count(dto?.progress?.required_completed),
    requiredTotal: count(dto?.progress?.required_total),
    optionalCompleted: count(dto?.progress?.optional_completed),
    optionalRequired: count(dto?.progress?.optional_required),
    isCompleted: dto?.progress?.is_completed === true,
    completedAt: dto?.progress?.completed_at ?? null
  },
  rewards: list(dto?.rewards).map(normalizeReward),
  taskCounts: {
    total: count(dto?.task_counts?.total),
    active: count(dto?.task_counts?.active),
    completed: count(dto?.task_counts?.completed),
    claimable: count(dto?.task_counts?.claimable)
  },
  recurrence: dto?.recurrence ?? RecurrenceValues.ONCE,
  cycleKey: text(dto?.cycle_key),
  cycleStartsAt: dto?.cycle_starts_at ?? null,
  cycleEndsAt: dto?.cycle_ends_at ?? null,
  meta: normalizeCampaignMeta(dto?.metadata)
});

const normalizeTaskProgress = (
  dto: TaskProgressDto | undefined
): TaskProgress | null => {
  if (!dto) return null;
  return {
    current: decimal(dto.current),
    target: decimal(dto.target),
    remaining: decimal(dto.remaining),
    percent: count(dto.percent),
    unit: text(dto.unit)
  };
};

/**
 * @param fallbackCampaignId the campaign the request was made against — the
 * task DTO is not documented to echo `campaign_id`, and every mutation route
 * needs one.
 */
const normalizeTaskGoal = (
  dto: { target?: string; unit?: string } | undefined
): TaskGoal | null =>
  dto ? { target: decimal(dto.target), unit: text(dto.unit) } : null;

export const normalizeTask = (
  dto: TaskViewDto,
  fallbackCampaignId = ''
): Task => {
  const claimStatus = dto?.claim?.status ?? ClaimStatusValues.NOT_AVAILABLE;
  const claimMode = dto?.claim_mode ?? ClaimModeValues.AUTO;
  return {
    id: text(dto?.id),
    campaignId: text(dto?.campaign_id) || fallbackCampaignId,
    title: text(dto?.title),
    subtitle: text(dto?.subtitle),
    category: text(dto?.category),
    activityStatus: dto?.activity_status ?? ActivityStatusValues.INACTIVE,
    userStatus: dto?.user_status ?? UserTaskStatusValues.NOT_STARTED,
    startsAt: dto?.starts_at ?? null,
    endsAt: dto?.ends_at ?? null,
    remainingSeconds: int64(dto?.remaining_seconds),
    progress: normalizeTaskProgress(dto?.progress),
    // A public task sends `goal`; a personal one has the same values in its
    // progress.
    goal: normalizeTaskGoal(dto?.goal ?? dto?.progress),
    claim: {
      // `required` is listed in the guide but not guaranteed in the payload; a
      // public task sends `claim_required` instead, and the claim mode says
      // the same thing when both are missing.
      required:
        dto?.claim?.required ??
        dto?.claim_required ??
        claimMode === ClaimModeValues.CLAIM_REQUIRED,
      status: claimStatus
    },
    rewards: list(dto?.rewards).map(normalizeReward),
    action: dto?.action
      ? {
          destination: toTaskDestination(dto.action.android_route),
          type: dto.action.type,
          value: text(dto.action.value),
          label: text(dto.action.label)
        }
      : null,
    instructions: list(dto?.instructions).filter(
      (step): step is string => typeof step === 'string'
    ),
    imageUrl: trustedImageUrl(text(dto?.image_url)) ?? '',
    selfCompletable: dto?.self_completable === true,
    rewardLockedByCampaign: dto?.reward_locked_by_campaign === true,
    rewardRequiresCampaignCompletion:
      dto?.reward_requires_campaign_completion === true,
    claimMode,
    recurrence: dto?.recurrence ?? RecurrenceValues.ONCE,
    cycleKey: text(dto?.cycle_key),
    metadata:
      dto?.metadata && typeof dto.metadata === 'object' ? dto.metadata : {}
  };
};

export const normalizeTaskMutationResult = (
  dto: TaskMutationResultDto,
  fallbackCampaignId = ''
): TaskMutationResult => ({
  alreadyApplied: dto?.already_applied === true,
  task: normalizeTask(dto?.task ?? ({} as TaskViewDto), fallbackCampaignId),
  rewards: list(dto?.rewards).map(normalizeReward)
});

const normalizePrize = (dto: LotteryPrizeDto): LotteryPrize => ({
  key: text(dto?.key),
  winnerCount: count(dto?.winner_count),
  asset: text(dto?.asset),
  amount: decimal(dto?.amount)
});

export const normalizeLotterySummary = (
  dto: LotterySummaryDto
): LotterySummary => ({
  cycleKey: text(dto?.cycle_key),
  cycleStartsAt: dto?.cycle_starts_at ?? null,
  cycleEndsAt: dto?.cycle_ends_at ?? null,
  userTicketCount: decimal(dto?.user_ticket_count),
  userEffectiveWeight: decimal(dto?.user_effective_weight),
  participantCount: count(dto?.participant_count),
  totalTicketCount: decimal(dto?.total_ticket_count),
  totalEffectiveWeight: decimal(dto?.total_effective_weight),
  drawn: dto?.drawn === true,
  drawId: text(dto?.draw_id),
  drawnAt: dto?.drawn_at ?? null,
  // Empty until the cycle is drawn — planned prizes are not in the contract.
  prizes: list(dto?.prizes).map(normalizePrize),
  remainingSeconds: int64(dto?.remaining_seconds)
});

export const normalizeLottery = (dto: LotteryListItemDto): Lottery => ({
  campaignId: text(dto?.campaign_id),
  campaignTitle: text(dto?.campaign_title),
  cycleKey: text(dto?.cycle_key),
  drawId: text(dto?.draw_id),
  drawnAt: dto?.drawn_at ?? null,
  participantCount: count(dto?.participant_count),
  prizes: list(dto?.prizes).map(normalizePrize)
});

/** Shape is unconfirmed pending the winners-endpoint fix — read defensively. */
export const normalizeWinner = (dto: LotteryWinnerDto): LotteryWinner => ({
  maskedIdentity: text(dto?.masked_identity) || '****',
  prizeKey: text(dto?.prize_key),
  asset: text(dto?.asset),
  amount: decimal(dto?.amount)
});

const normalizeTaskHistoryEntry = (
  dto: TaskHistoryEntryDto
): TaskHistoryEntry => ({
  campaignId: text(dto?.campaign_id),
  campaignTitle: text(dto?.campaign_title),
  taskId: text(dto?.task_id),
  title: text(dto?.task_title),
  cycleKey: text(dto?.cycle_key),
  activityStatus: dto?.activity_status ?? ActivityStatusValues.INACTIVE,
  userStatus: dto?.user_status ?? UserTaskStatusValues.NOT_STARTED,
  claimStatus: dto?.claim?.status ?? ClaimStatusValues.NOT_AVAILABLE,
  completedAt: dto?.completed_at ?? null,
  updatedAt: dto?.updated_at ?? null
});

const normalizeRewardHistoryEntry = (
  dto: RewardHistoryEntryDto
): RewardHistoryEntry => {
  const asset = text(dto?.asset);
  const amount = decimal(dto?.amount);
  return {
    key: text(dto?.key),
    asset,
    amount,
    displayName: text(dto?.display_name) || formatRewardLabel(amount, asset),
    status: text(dto?.status),
    sourceType: text(dto?.source_type),
    taskId: text(dto?.task_id),
    campaignId: text(dto?.campaign_id),
    campaignTitle: text(dto?.campaign_title),
    cycleKey: text(dto?.cycle_key),
    campaignCycleKey: text(dto?.campaign_cycle_key) || text(dto?.cycle_key),
    // `issued_at` is when a ticket reached the user; `created_at` is the
    // fallback for rewards that are still PENDING.
    grantedAt: dto?.granted_at ?? dto?.issued_at ?? dto?.created_at ?? null
  };
};

const normalizeCampaignHistoryEntry = (
  dto: CampaignHistoryEntryDto
): CampaignHistoryEntry => ({
  campaignId: text(dto?.campaign_id),
  campaignTitle: text(dto?.campaign_title),
  cycleKey: text(dto?.cycle_key),
  completedAt: dto?.completed_at ?? null,
  rewards: list(dto?.rewards).map(normalizeReward)
});

/**
 * History is a tagged union; only the field matching `type` is populated. An
 * item whose payload is missing is dropped rather than rendered as a blank row.
 */
export const normalizeHistoryItem = (
  dto: HistoryItemDto
): HistoryItem | null => {
  switch (dto?.type) {
    case 'TASK':
      return dto.task
        ? { type: 'TASK', task: normalizeTaskHistoryEntry(dto.task) }
        : null;
    case 'REWARD':
      return dto.reward
        ? { type: 'REWARD', reward: normalizeRewardHistoryEntry(dto.reward) }
        : null;
    case 'CAMPAIGN':
      return dto.campaign
        ? {
            type: 'CAMPAIGN',
            campaign: normalizeCampaignHistoryEntry(dto.campaign)
          }
        : null;
    default:
      return null;
  }
};

/** V2 never sends `total_pages`; it is derived from `total` and `page_limit`. */
export const normalizePage = <D, T>(
  body: ListBodyDto<D> | undefined,
  map: (item: D) => T
): Paged<T> => {
  const pageLimit = count(body?.page_limit) || DEFAULT_PAGE_LIMIT;
  const total = count(body?.total);
  return {
    items: list(body?.items).map(map),
    page: count(body?.page) || 1,
    pageLimit,
    total,
    totalPages: pageLimit > 0 ? Math.ceil(total / pageLimit) : 0
  };
};

/** `transformResponse` helper for the `{ data: { items, … } }` list envelope. */
export const normalizeListEnvelope = <D, T>(
  response: ListEnvelopeDto<D>,
  map: (item: D) => T
): Paged<T> => normalizePage(unwrap(response), map);

export const normalizeSpecialCampaign = (
  response: { data: SpecialCampaignEnvelopeDto } | undefined
): Campaign | null => {
  const campaign = response?.data?.campaign;
  return campaign ? normalizeCampaign(campaign) : null;
};

/**
 * Items whose union payload did not match their `type` are filtered out; the
 * page counters still describe the server-side page.
 */
export const normalizeHistoryPage = (
  body: ListBodyDto<HistoryItemDto> | undefined
): Paged<HistoryItem> => {
  const page = normalizePage(body, normalizeHistoryItem);
  return {
    ...page,
    items: page.items.filter((item): item is HistoryItem => item !== null)
  };
};
